import { supabase } from "./supabase";

/**
 * File Upload Service for KYC Documents
 * Handles uploading files to Supabase Storage bucket
 */
export class FileUploadService {
  static BUCKET_NAME = "kyc-documents";

  /**
   * Upload a file to Supabase Storage
   * @param {File} file - The file to upload
   * @param {string} sessionId - KYC session ID for organizing files
   * @param {string} documentType - Type of document (e.g., 'idDocument', 'proofOfAddress')
   * @returns {Promise} Upload result with file URL or error
   */
  static async uploadFile(file, sessionId, documentType) {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      // Generate unique file path
      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop();
      const fileName = `${sessionId}/${documentType}_${timestamp}.${fileExtension}`;

      console.log("Uploading file:", fileName);

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Supabase storage error:", error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          path: data.path,
          fullPath: data.fullPath,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error("File upload error:", error);
      return { success: false, error: "Failed to upload file" };
    }
  }

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  static validateFile(file) {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!file) {
      return { isValid: false, error: "No file provided" };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: "Invalid file type. Please upload JPEG, PNG, or PDF files only.",
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: "File too large. Maximum size is 10MB.",
      };
    }

    return { isValid: true };
  }

  /**
   * Delete a file from Supabase Storage
   * @param {string} filePath - Path of the file to delete
   * @returns {Promise} Deletion result
   */
  static async deleteFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([filePath]);

      if (error) {
        console.error("File deletion error:", error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error("File deletion error:", error);
      return { success: false, error: "Failed to delete file" };
    }
  }

  /**
   * Check if storage bucket is accessible
   * @returns {Promise} Bucket accessibility result
   */
  static async checkBucketAccess() {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .list("", { limit: 1 });

      if (error) {
        console.error("Bucket access error:", error);
        return { success: false, error: error.message };
      }

      return { success: true, message: "Bucket is accessible" };
    } catch (error) {
      console.error("Bucket access error:", error);
      return { success: false, error: "Failed to access storage bucket" };
    }
  }
}
